/**
 * The raid screen's own widgets: the live strip (scene, bars, what you have
 * found, what just happened), the four focus buttons, and the little stat chips
 * the launch and result strips share.
 *
 * Split out of `world-map-ui.ts`, which is about the map. Janeks, 2026-10-14:
 * *"Draw every piece of info as UI element not just naked text."* — so a raid
 * number is a bar, a badge or a chip here, never a sentence.
 *
 * ─── Built once, updated in place ───────────────────────────────────────────
 * The footer re-renders on every store change, and a raid polls every couple of
 * seconds. Rebuilding the strip each time would restart the walking video from
 * frame zero and drop a half-made discard selection. So the live strip is
 * created once per raid and `update()`d: the video node is never touched, and
 * the found-items grid only repaints when its contents actually changed.
 *
 * No hex and no SVG paint attributes in this file: colour is CSS
 * (`styles/raid-panel.css.ts`), icons are generated images, not drawn here.
 */
import { discardRaidItem, extractRaid } from '../../state/raid-state';
import type {
  RaidFocusKind, RaidItemInfo, RaidState, RaidStepResult,
} from '../../state/raid-state.types';
import { capacityLevel, fillPercent } from './capacity';
import { emptySlotHtml, slotGridStyle, slotHtml } from './item-slot';
import { itemIconUrl } from './quest-art';
import { createExtractPicker, formatDuration } from './raid-extract';
import { escapeHtml as esc } from '../../util';

/**
 * Real time out on this raid, from the server's clock. Janeks, 2026-10-19:
 * *"Convert raid hours to real time in raid."* While the player chooses what to
 * carry home the run is frozen, so the clock stops at the Extract press.
 */
export function raidElapsedMs(raid: Pick<RaidState, 'choosing' | 'step' | 'stepDurationMs' | 'startedAt' | 'clockOffsetMs'>, nowMs = Date.now()): number {
  if (raid.choosing) return raid.step * raid.stepDurationMs;
  return Math.max(0, nowMs + raid.clockOffsetMs - raid.startedAt);
}

/** Real time from launch to dusk, after which danger keeps climbing. */
export function raidDuskMs(raid: Pick<RaidState, 'daylightBudget' | 'stepsPerHour' | 'stepDurationMs'>): number {
  return raid.daylightBudget * Math.max(1, raid.stepsPerHour) * raid.stepDurationMs;
}

/** Round UP to one decimal: 3.41 -> 3.5, 3 -> 3. The +1e-9 keeps float noise (3.4000000001) from tipping a clean 3.4 up. */
export function ceil1(n: number): number {
  return Math.ceil(n * 10 - 1e-9) / 10;
}

export const FOCUS_LABELS: Record<RaidFocusKind, string> = {
  FOOD: 'Food',
  SCRAP: 'Scrap',
  BOUNTY: 'Bounty',
  EXPLORE: 'Explore',
};

// ─── What each focus does ────────────────────────────────────────────────────

/**
 * How a focus bends the run, in the player's words. `2` is a lot more likely,
 * `1` somewhat, `-1` somewhat less, `-2` a lot less — never "none": a minus is
 * a lower chance, not a ban (Janeks).
 *
 * These are read off what the server really does, not invented:
 *  - loot tags follow the per-focus loot tables in `prisma/seed.ts` (FOOD
 *    tables hold food, water and meds; SCRAP holds scrap, planks, gear and a
 *    little ammo; BOUNTY holds guns and ammo; EXPLORE a thin mix);
 *  - "Encounters" and "Map reveal" follow `focusWeights` in
 *    `raid.config.ts` (BOUNTY weights events 5, EXPLORE 3 and traverse 6, the
 *    rest 2).
 * If either is retuned, this is the list to revisit.
 */
export type FxLevel = 2 | 1 | -1 | -2;
export interface FocusFx {
  tag: string;
  level: FxLevel;
  /** An encounter is neither good nor bad news, so it reads amber rather than green/red. */
  risk?: true;
}

export const FOCUS_EFFECTS: Record<RaidFocusKind, FocusFx[]> = {
  FOOD: [
    { tag: 'Food', level: 2 },
    { tag: 'Meds', level: 1 },
    { tag: 'Scrap', level: -1 },
    { tag: 'Weapons', level: -1 },
  ],
  SCRAP: [
    { tag: 'Scrap', level: 2 },
    { tag: 'Gear', level: 1 },
    { tag: 'Weapons', level: 1 },
    { tag: 'Food', level: -1 },
    { tag: 'Meds', level: -1 },
  ],
  BOUNTY: [
    { tag: 'Encounters', level: 2, risk: true },
    { tag: 'Weapons', level: 2 },
    { tag: 'Ammo', level: 2 },
    { tag: 'Supplies', level: -1 },
  ],
  // Janeks, 2026-10-19: location discovery is Explore's ONLY upside. Every raid
  // banks time toward the next zone; Explore banks it 1.5x (`exploreFocusMult`).
  EXPLORE: [
    { tag: 'Location discovery ×1.5', level: 1 },
    { tag: 'Encounters', level: 1, risk: true },
    { tag: 'Loot', level: -1 },
  ],
};

const ARROWS: Record<FxLevel, string> = { 2: '▲▲', 1: '▲', [-1]: '▼', [-2]: '▼▼' };
const WORDS: Record<FxLevel, string> = {
  2: 'much more likely', 1: 'more likely', [-1]: 'less likely', [-2]: 'much less likely',
};

function fxChip(fx: FocusFx): HTMLElement {
  const c = document.createElement('span');
  c.className = `bv-fx ${fx.level > 0 ? 'bv-fx-up' : 'bv-fx-down'}${fx.risk ? ' bv-fx-risk' : ''}`;
  c.setAttribute('aria-label', `${fx.tag}: ${WORDS[fx.level]}`);
  c.textContent = `${fx.tag} ${ARROWS[fx.level]}`;
  return c;
}

/**
 * One focus choice: the generated plate behind it, the name over it, and under
 * the name what it does. `data-focus` is the stable handle (the label is for
 * people).
 */
export function focusButton(focus: RaidFocusKind, disabled: boolean, onPick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'bv-act bv-focus';
  b.dataset.focus = focus;
  b.disabled = disabled;

  const name = document.createElement('span');
  name.className = 'bv-focus-name';
  name.textContent = FOCUS_LABELS[focus];

  const fx = document.createElement('span');
  fx.className = 'bv-focus-fx';
  fx.append(...FOCUS_EFFECTS[focus].map(fxChip));

  b.append(name, fx);
  b.onclick = onPick;
  return b;
}

// ─── Small shared pieces ─────────────────────────────────────────────────────

export type ChipTone = 'plain' | 'good' | 'warn' | 'bad';

/** A label/value chip: `[ HAUL  6 items ]`. The unit of "info as UI". */
export function statChip(label: string, value: string, tone: ChipTone = 'plain', icon?: string): HTMLElement {
  const c = document.createElement('span');
  c.className = `bv-chip bv-chip-${tone}`;
  const l = document.createElement('small');
  l.textContent = label;
  const v = document.createElement('b');
  if (icon) v.insertAdjacentHTML('afterbegin', icon);
  v.append(value);
  // The space is invisible in the flex row but keeps textContent readable (and testable).
  c.append(l, ' ', v);
  return c;
}

/** A row of chips. */
export function chipRow(...chips: HTMLElement[]): HTMLElement {
  const r = document.createElement('div');
  r.className = 'bv-chip-row';
  r.append(...chips);
  return r;
}


/**
 * Icons for chips and bars: small generated pieces in `public/icons/ui/`
 * (energy, hours, xp, weight, death, map) plus the existing backpack. Never
 * drawn here — a missing name is a typo, not a fallback.
 */
export type ChipIcon = 'energy' | 'hours' | 'xp' | 'weight' | 'death' | 'map' | 'pack';

export function chipIcon(name: ChipIcon): string {
  const src = name === 'pack' ? '/icons/ui/backpack.webp' : `/icons/ui/chip-${name}.png`;
  return `<img class="bv-ico" src="${src}" alt="" draggable="false">`;
}

function humanise(key: string): string {
  const t = key.replace(/[_-]+/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function nameOf(items: Record<string, RaidItemInfo>, key: string): string {
  return items[key]?.name ?? humanise(key);
}

// ─── Zones ───────────────────────────────────────────────────────────────────

/** Tier -> the class that paints it the same colour as its zone on the map (moss / warn / rust). */
export function tierClass(tier: number): string {
  return `bv-tier-${tier >= 3 ? 3 : tier <= 1 ? 1 : 2}`;
}

/** A tier as a coloured badge: `[ TIER 2 ]`. */
export function tierChip(tier: number): HTMLElement {
  const c = document.createElement('span');
  c.className = `bv-chip bv-tier-chip ${tierClass(tier)}`;
  const l = document.createElement('small');
  l.textContent = 'Tier';
  const v = document.createElement('b');
  v.textContent = String(tier);
  c.append(l, ' ', v);
  return c;
}

/**
 * The raid screen's heading: `Raid <Zone> for scrap?`, with the zone bold,
 * underlined and in its tier's map colour. `rest` is what follows the name.
 */
export function raidTitle(prefix: string, zone: string | null, tier: number, rest = ''): HTMLElement {
  const h = document.createElement('p');
  h.className = 'bv-raid-title';
  h.append(`${prefix} `);
  const u = document.createElement('u');
  u.className = zone ? tierClass(tier) : '';
  u.textContent = zone ?? 'a zone';
  h.append(u);
  if (rest) h.append(rest);
  return h;
}

/**
 * The energy bar, drawn exactly like the HUD's EN bar, with the cost of the
 * raid shaded onto the end of the filled part so the player sees what is about
 * to be spent rather than reading it. Values are whole numbers: regen leaves
 * energy fractional and a fractional energy figure is noise.
 */
export function energyCostBar(energy: number, max: number, cost: number): HTMLElement {
  const have = Math.floor(energy);
  const pct = (n: number): number => (max > 0 ? Math.max(0, Math.min(100, (n / max) * 100)) : 0);
  const row = document.createElement('div');
  row.className = 'bv-hud-row bv-raid-energy';
  row.innerHTML = `<span class="bv-hud-label">${chipIcon('energy')}</span>`
    + '<div class="bv-hud-bar"><div class="bv-hud-fill bv-hud-fill-en"></div>'
    + '<div class="bv-raid-cost-shade"></div><span class="bv-hud-inbar"></span></div>';
  row.querySelector<HTMLElement>('.bv-hud-fill')!.style.width = `${pct(have)}%`;
  const shade = row.querySelector<HTMLElement>('.bv-raid-cost-shade')!;
  const spend = Math.min(cost, have);
  shade.style.left = `${pct(have - spend)}%`;
  shade.style.width = `${pct(spend)}%`;
  row.querySelector<HTMLElement>('.bv-hud-inbar')!.textContent = `${have}/${Math.floor(max)}`;
  return row;
}

// ─── The run log ─────────────────────────────────────────────────────────────

export interface RaidEvent {
  tone: 'good' | 'warn' | 'bad' | 'plain';
  text: string;
}

/**
 * What one step means to the player, or `null` for a step with nothing worth a
 * line (a plain stretch of walking). Pure so the wording is testable.
 */
export function describeStep(
  step: RaidStepResult,
  items: Record<string, RaidItemInfo>,
): RaidEvent[] {
  const out: RaidEvent[] = [];
  const enc = step.encounter;
  if (enc) {
    const who = humanise(enc.encounterKey);
    if (enc.outcome === 'fled') out.push({ tone: 'warn', text: `Slipped away from ${who}` });
    else if (enc.outcome === 'won') {
      out.push({ tone: enc.hpLoss > 0 ? 'warn' : 'good', text: `Beat ${who}${enc.hpLoss > 0 ? ` · −${enc.hpLoss} HP` : ''}` });
    } else out.push({ tone: 'bad', text: `Mauled by ${who} · −${enc.hpLoss} HP` });
  }
  if (step.heal) {
    out.push({ tone: 'good', text: `Used ${nameOf(items, step.heal.itemKey)} · +${step.heal.healed} HP` });
  }
  for (const it of step.loot?.items ?? []) {
    out.push({ tone: it.isJunk ? 'plain' : 'good', text: `Found ${nameOf(items, it.itemKey)}${it.qty > 1 ? ` ×${it.qty}` : ''}` });
  }
  for (const it of step.loot?.leftBehind ?? []) {
    out.push({ tone: 'warn', text: `No room for ${nameOf(items, it.itemKey)}${it.qty > 1 ? ` ×${it.qty}` : ''}` });
  }
  return out;
}

/** The latest `max` events, newest first. */
export function recentEvents(
  steps: readonly RaidStepResult[],
  items: Record<string, RaidItemInfo>,
  max = 30,
): RaidEvent[] {
  const out: RaidEvent[] = [];
  for (let i = steps.length - 1; i >= 0 && out.length < max; i -= 1) {
    const evs = describeStep(steps[i]!, items);
    // Within one step, later lines happened later; show them newest-first too.
    for (let j = evs.length - 1; j >= 0 && out.length < max; j -= 1) out.push(evs[j]!);
  }
  return out;
}

// ─── The live strip ──────────────────────────────────────────────────────────

const WALK_VIDEO = '/ui/raid/walk-loop.mp4';
const WALK_POSTER = '/ui/raid/walk-poster.jpg';

export interface ActiveRaidView {
  root: HTMLElement;
  update(raid: RaidState): void;
}

function reducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

/** The one looping clip the whole scene is: a survivor walking in place while the ruins slide past. */
function walkScene(): HTMLElement {
  const scene = document.createElement('div');
  scene.className = 'bv-raid-scene';
  scene.setAttribute('aria-hidden', 'true');

  const video = document.createElement('video');
  video.className = 'bv-raid-video';
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.poster = WALK_POSTER;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.src = WALK_VIDEO;
  scene.appendChild(video);

  // A frozen poster for anyone who asked their system for less motion. The
  // attribute rather than a play() call: a muted autoplay clip starts by
  // itself, and a blocked one simply leaves the poster up.
  video.autoplay = !reducedMotion();
  return scene;
}

/**
 * The bars are the HUD's own markup and classes (`bv-hud-row` / `-bar` / `-fill`
 * / `-inbar`), so HP here looks exactly like HP up in the corner. HP keeps the
 * HUD's red fill; LOAD takes the shared capacity ramp (moss -> warn -> rust).
 */
function barHtml(kind: 'hp' | 'carry' | 'hour', label: string): string {
  const fill = kind === 'hp' ? 'bv-hud-fill bv-hud-fill-hp'
    : kind === 'hour' ? 'bv-hud-fill bv-hud-fill-xp'
      : 'bv-hud-carry-fill';
  return `<div class="bv-hud-row bv-raid-row" data-gauge="${kind}">`
    + `<span class="bv-hud-label">${label}</span>`
    + `<div class="bv-hud-bar" role="meter"><div class="${fill}"></div><span class="bv-hud-inbar"></span></div>`
    + '</div>';
}

function setBar(root: HTMLElement, kind: 'hp' | 'carry' | 'hour', used: number, max: number, text: string, level?: string): void {
  const g = root.querySelector<HTMLElement>(`[data-gauge="${kind}"]`)!;
  g.classList.remove('bv-cap-ok', 'bv-cap-warn', 'bv-cap-critical');
  if (level) g.classList.add(`bv-cap-${level}`);
  g.querySelector<HTMLElement>('[class*="-fill"]')!.style.width = `${fillPercent(used, max)}%`;
  g.querySelector<HTMLElement>('.bv-hud-inbar')!.textContent = text;
  const bar = g.querySelector<HTMLElement>('.bv-hud-bar')!;
  bar.setAttribute('aria-valuenow', String(Math.round(used)));
  bar.setAttribute('aria-valuemax', String(Math.round(max)));
}

export function createActiveRaidView(): ActiveRaidView {
  const root = document.createElement('div');
  root.className = 'bv-raid';

  root.appendChild(walkScene());

  const scene = root.querySelector<HTMLElement>('.bv-raid-scene')!;
  scene.insertAdjacentHTML('beforeend',
    '<span class="bv-raid-badge bv-raid-badge-focus"></span>'
    + `<span class="bv-raid-badge bv-raid-badge-hours">${chipIcon('hours')}<b class="bv-raid-hours">0</b><small>out</small></span>`);

  const rest = document.createElement('div');
  rest.className = 'bv-raid-body';
  rest.innerHTML = barHtml('hp', 'HP')
    + barHtml('carry', 'LOAD')
    + barHtml('hour', 'DUSK')
    + '<div class="bv-found">'
    + '<div class="bv-found-head"><span class="bv-found-title">Found</span><b class="bv-found-count">0</b>'
    + '<span class="bv-found-full" hidden>Bag full — drop something</span></div>'
    + `<div class="bv-found-grid" style="${slotGridStyle(5)}"></div>`
    + '<div class="bv-found-pick" hidden></div>'
    + '</div>'
    + '<ul class="bv-feed" aria-label="Recent events"></ul>'
    + '<p class="bv-win-err" hidden></p>';
  root.appendChild(rest);

  const extract = document.createElement('button');
  extract.type = 'button';
  // Moss, not brass: extraction is the "get out safely" action, and the kit
  // reserves the green status token for exactly that.
  extract.className = 'bv-act bv-wm-extract';
  extract.textContent = 'Extract';
  extract.onclick = () => { void extractRaid().catch(() => {}); };
  root.appendChild(extract);

  // Rules 2: Extract opens the choice of what to carry, in place of the strip.
  const picker = createExtractPicker();
  picker.root.hidden = true;
  root.appendChild(picker.root);

  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;
  const grid = $('.bv-found-grid');
  const pick = $('.bv-found-pick');
  const feed = $('.bv-feed');

  let selectedId: string | null = null;
  let gridSig = '';
  let feedSig = '';
  let latest: RaidState | null = null;
  let shownError: string | null = null;

  const render = (): void => {
    const raid = latest;
    if (!raid) return;
    const bag = raid.bag;
    if (selectedId && !bag.some((i) => i.id === selectedId)) selectedId = null;

    // Slot grid: one row minimum, growing by whole rows like every other grid.
    const sig = `${bag.map((i) => `${i.id}x${i.qty}`).join(',')}|${selectedId ?? ''}`;
    if (sig !== gridSig) {
      gridSig = sig;
      const total = Math.max(5, Math.ceil(bag.length / 5) * 5);
      const cells = bag.map((it) => {
        const art = itemIconUrl(it.itemKey);
        const name = nameOf(raid.items, it.itemKey);
        return slotHtml(
          {
            itemId: it.id, itemKey: it.itemKey, name, iconPath: raid.items[it.itemKey]?.iconPath ?? null, quantity: it.qty,
          },
          art ? `<img src="${esc(art)}" alt="" draggable="false">` : '',
        );
      });
      while (cells.length < total) cells.push(emptySlotHtml());
      grid.innerHTML = cells.join('');
      grid.querySelectorAll<HTMLElement>('.bv-slot-filled').forEach((el) => {
        const it = bag.find((b) => b.id === el.dataset.itemId);
        if (!it) return;
        el.tabIndex = 0;
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', `${nameOf(raid.items, it.itemKey)}, select to drop`);
        el.setAttribute('aria-pressed', String(it.id === selectedId));
        if (it.isJunk) el.dataset.junk = 'true';
        if (it.id === selectedId) el.classList.add('bv-slot-picked');
      });
    }

    $('.bv-found-count').textContent = `${bag.length} ${bag.length === 1 ? 'item' : 'items'}`;

    const sel = selectedId ? bag.find((i) => i.id === selectedId) : undefined;
    if (sel) {
      pick.hidden = false;
      // The same art the picked slot shows, so the two can never disagree
      // about which image an item has.
      const art = grid.querySelector<HTMLImageElement>(`.bv-slot-filled[data-item-id="${CSS.escape(sel.id)}"] img`)?.getAttribute('src');
      pick.innerHTML = `<span class="bv-found-pick-art" aria-hidden="true">${art ? `<img src="${esc(art)}" alt="" draggable="false">` : ''}</span>`
        + `<span class="bv-found-pick-name">${esc(nameOf(raid.items, sel.itemKey))}`
        + `${sel.qty > 1 ? ` ×${sel.qty}` : ''}</span>`
        + `<span class="bv-chip bv-chip-plain"><small>Weight</small><b>${ceil1(sel.weight)} kg</b></span>`
        + '<button type="button" class="bv-act bv-act-danger bv-found-drop">Drop</button>';
      pick.querySelector<HTMLButtonElement>('.bv-found-drop')!.onclick = () => {
        const id = sel.id;
        selectedId = null;
        void discardRaidItem(id).catch(() => {});
      };
    } else {
      pick.hidden = true;
      pick.innerHTML = '';
    }

    const lastStep = raid.steps.length ? raid.steps[raid.steps.length - 1]!.step : -1;
    const fsig = `${lastStep}|${raid.steps.length}|${bag.length}`;
    if (fsig !== feedSig) {
      feedSig = fsig;
      const events = recentEvents(raid.steps, raid.items);
      feed.innerHTML = events.length
        ? events.map((e) => `<li class="bv-feed-row bv-feed-${e.tone}"><i></i><span>${esc(e.text)}</span></li>`).join('')
        : '<li class="bv-feed-row bv-feed-plain"><i></i><span>Setting out…</span></li>';
    }
  };

  grid.addEventListener('click', (e) => {
    const slot = (e.target as HTMLElement).closest<HTMLElement>('.bv-slot-filled');
    if (!slot) return;
    const id = slot.dataset.itemId ?? null;
    selectedId = selectedId === id ? null : id;
    gridSig = ''; // force the repaint that moves the highlight
    render();
  });
  grid.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const slot = (e.target as HTMLElement).closest<HTMLElement>('.bv-slot-filled');
    if (!slot) return;
    e.preventDefault();
    slot.click();
  });

  return {
    root,
    update(raid) {
      latest = raid;
      const opening = raid.choosing && picker.root.hidden;
      rest.hidden = raid.choosing;
      extract.hidden = raid.choosing;
      picker.root.hidden = !raid.choosing;
      if (raid.choosing) picker.update(raid);
      // The Extract button sat at the bottom of a scrolled side panel; the
      // picker opens there too, so bring its heading and gauges into view.
      if (opening) picker.root.scrollIntoView?.({ block: 'start' });

      const elapsed = raidElapsedMs(raid);
      $('.bv-raid-hours').textContent = formatDuration(elapsed);
      $('.bv-raid-badge-focus').textContent = raid.focus ? FOCUS_LABELS[raid.focus] : '';

      setBar(root, 'hp', raid.hp, raid.maxHp, `${Math.max(0, Math.ceil(raid.hp))}/${Math.ceil(raid.maxHp)}`);
      setBar(root, 'carry', raid.bagWeight, raid.carryWeightCap,
        `${ceil1(raid.bagWeight)}/${ceil1(raid.carryWeightCap)} kg`, capacityLevel(raid.bagWeight, raid.carryWeightCap));
      // Real time to dusk; past it, how long the night has been going.
      const dusk = raidDuskMs(raid);
      setBar(root, 'hour', Math.min(elapsed, dusk), dusk,
        elapsed < dusk ? `dusk in ${formatDuration(dusk - elapsed)}` : `night · ${formatDuration(elapsed - dusk)}`,
        elapsed < dusk ? undefined : 'critical');

      const full = $('.bv-found-full');
      full.hidden = capacityLevel(raid.bagWeight, raid.carryWeightCap) !== 'critical';
      // Rules 2 leaves nothing behind: over the cap is fine until Extract.
      full.textContent = raid.rulesVersion >= 2 ? 'Over the bag: choose at Extract' : 'Bag full — drop something';

      // Touch the plate only when the message CHANGES. Rewriting identical text
      // on every poll counts as a mutation to the error watcher, which replays
      // the shake and the sound each time — a failing poll would nag every 2 s.
      if (raid.error !== shownError) {
        shownError = raid.error;
        const err = $('.bv-win-err');
        err.hidden = !raid.error;
        err.textContent = raid.error ?? '';
      }

      render();
    },
  };
}
